import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  startAfter,
  limit,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import {
  trackError,
  ERROR_CODES,
  createErrorMeta,
  assertWriteAccess,
  trackUsage,
} from '../../lib/telemetry';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../lib/firebase';
import { Expense, ExpenseCategory } from '../../types/models';
import { compressReceipt } from '../../lib/compression';
import { logReceiptUploadAudit } from '../../lib/firebase';
import { GSTCalculator } from '../../lib/calculateGST';

interface ExpenseState {
  expenses: Expense[];
  isLoading: boolean;
  error: string | null;
  totalExpenses: number;
  totalITCs: number;
  filters: {
    category?: ExpenseCategory;
    dateRange?: { start: Date; end: Date };
    searchTerm?: string;
  };
  paging?: {
    nextCursor: { dateMs: number; uploadedAtMs: number } | null;
  };
  uploadState: {
    status: 'idle' | 'compressing' | 'uploading' | 'stalled' | 'failed' | 'done';
    progress: number;
    error: string | null;
    lastProgressTime: number;
  };
}

const initialState: ExpenseState = {
  expenses: [],
  isLoading: false,
  error: null,
  totalExpenses: 0,
  totalITCs: 0,
  filters: {},
  paging: {
    nextCursor: null,
  },
  uploadState: {
    status: 'idle',
    progress: 0,
    error: null,
    lastProgressTime: Date.now(),
  },
};

// Async thunks
export const fetchExpenses = createAsyncThunk(
  'expenses/fetchExpenses',
  async (userId: string, { rejectWithValue }) => {
    try {
      const expensesRef = collection(db, 'users', userId, 'expenses');
      const q = query(expensesRef, orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);

      const expenses: Expense[] = [];
      querySnapshot.forEach(doc => {
        const data = doc.data();
        expenses.push({
          id: doc.id,
          ...data,
          date: data.date.toDate(),
          uploadedAt: data.uploadedAt,
        } as Expense);
      });

      return expenses;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Paged fetch with optional date range, ordered by date desc then uploadedAt desc
export const fetchExpensesPaged = createAsyncThunk(
  'expenses/fetchExpensesPaged',
  async (
    {
      userId,
      pageSize = 10,
      after,
      dateRange,
    }: {
      userId: string;
      pageSize?: number;
      after?: { dateMs: number; uploadedAtMs: number };
      dateRange?: { start: Date; end: Date };
    },
    { rejectWithValue }
  ) => {
    try {
      const expensesRef = collection(db, 'users', userId, 'expenses');
      const constraints: any[] = [];
      if (dateRange?.start && dateRange?.end) {
        constraints.push(where('date', '>=', Timestamp.fromDate(dateRange.start)));
        constraints.push(where('date', '<=', Timestamp.fromDate(dateRange.end)));
      }
      constraints.push(orderBy('date', 'desc'));
      constraints.push(orderBy('uploadedAt', 'desc'));
      let qBase = query(expensesRef, ...constraints, limit(pageSize + 1));

      if (after) {
        const dateTs = Timestamp.fromMillis(after.dateMs);
        const uploadedTs = Timestamp.fromMillis(after.uploadedAtMs);
        qBase = query(
          expensesRef,
          ...constraints,
          startAfter(dateTs, uploadedTs),
          limit(pageSize + 1)
        );
      }

      const snapshot = await getDocs(qBase);
      const docs = snapshot.docs;
      const hasExtra = docs.length > pageSize;
      const pageDocs = hasExtra ? docs.slice(0, pageSize) : docs;

      const items: Expense[] = pageDocs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          date: data.date.toDate(),
          uploadedAt: data.uploadedAt,
        } as Expense;
      });

      let nextCursor: { dateMs: number; uploadedAtMs: number } | null = null;
      if (hasExtra) {
        const last = pageDocs[pageDocs.length - 1];
        const lastData = last.data();
        nextCursor = {
          dateMs: lastData.date.toMillis(),
          uploadedAtMs: lastData.uploadedAt.toMillis(),
        };
      }

      return { items, nextCursor };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const addExpense = createAsyncThunk(
  'expenses/addExpense',
  async (
    {
      userId,
      expenseData,
      receiptFile,
      provinceCode,
    }: {
      userId: string;
      expenseData: Omit<
        Expense,
        'id' | 'userId' | 'uploadedAt' | 'receiptPhotoURL' | 'claimableITC'
      >;
      receiptFile?: File;
      provinceCode: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    const uploadStartTime = Date.now();
    let expenseId = `temp_${Date.now()}`; // Temporary ID for audit logging

    try {
      let receiptPhotoURL: string | undefined;
      let compressionResult: any = null;

      // Upload receipt image if provided
      if (receiptFile) {
        // Update progress state to compressing
        dispatch(
          setUploadState({
            status: 'compressing',
            progress: 10,
            error: null,
            lastProgressTime: Date.now(),
          })
        );

        try {
          // Use new compression function
          compressionResult = await compressReceipt(receiptFile, {
            targetMaxSizeKB: 350,
            stripExif: true,
          });

          // Update progress to uploading
          dispatch(
            setUploadState({
              status: 'uploading',
              progress: 50,
              error: null,
              lastProgressTime: Date.now(),
            })
          );

          // Check if user has Pro access before attempting upload
          const currentUser = auth.currentUser;
          if (!currentUser) {
            throw new Error('User not authenticated. Please refresh the page and try again.');
          }

          console.log('[Receipt Upload] Checking Pro access before upload...');

          // Force token refresh to ensure latest custom claims
          await currentUser.getIdToken(true);
          let tokenResult = await currentUser.getIdTokenResult();

          console.log('[Receipt Upload] Token claims:', {
            hasPro: tokenResult.claims.hasPro,
            hasArchive: tokenResult.claims.hasArchive,
            isAdmin: tokenResult.claims.isAdmin,
            issuedAt: tokenResult.issuedAtTime || 'unknown',
          });

          // If hasPro is not present, wait 2 seconds and try ONE more time
          if (!tokenResult.claims.hasPro) {
            console.warn(
              '[Receipt Upload] ⚠️ hasPro claim not found, waiting 2s and retrying token refresh...'
            );
            await new Promise(resolve => setTimeout(resolve, 2000));
            await currentUser.getIdToken(true); // Force refresh again
            tokenResult = await currentUser.getIdTokenResult();

            console.log('[Receipt Upload] Retry - Token claims:', {
              hasPro: tokenResult.claims.hasPro,
              hasArchive: tokenResult.claims.hasArchive,
              isAdmin: tokenResult.claims.isAdmin,
              issuedAt: tokenResult.issuedAtTime || 'unknown',
            });

            if (!tokenResult.claims.hasPro) {
              console.error('[Receipt Upload] ❌ hasPro claim STILL not found after retry');
              throw new Error(
                'Pro subscription not detected. Please visit your Profile page to sync your subscription, then try again.'
              );
            }
          }

          console.log('[Receipt Upload] ✅ hasPro claim verified, proceeding with upload');

          const storageRef = ref(
            storage,
            `receipts/${userId}/${Date.now()}_${compressionResult.mime.replace('/', '.')}`
          );

          console.log('[Receipt Upload] Uploading to:', storageRef.fullPath);
          const uploadResult = await uploadBytes(storageRef, compressionResult.blob);
          console.log('[Receipt Upload] ✅ Upload successful');

          receiptPhotoURL = await getDownloadURL(uploadResult.ref);
          console.log('[Receipt Upload] ✅ Download URL obtained');

          // Track storage usage in user document
          try {
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const currentUsage = userDoc.data()?.storageUsage || { totalBytes: 0, receiptCount: 0 };

            await updateDoc(userRef, {
              storageUsage: {
                totalBytes: currentUsage.totalBytes + compressionResult.blob.size,
                receiptCount: currentUsage.receiptCount + 1,
                lastUpdated: serverTimestamp(),
              },
            });
          } catch (storageTrackingError) {
            // Log but don't fail the upload if storage tracking fails
            console.warn('Failed to update storage usage tracking:', storageTrackingError);
          }

          // Update progress to done
          dispatch(
            setUploadState({
              status: 'done',
              progress: 100,
              error: null,
              lastProgressTime: Date.now(),
            })
          );
        } catch (compressionError: any) {
          // Determine error type for better user feedback
          let errorType = 'unknown';
          let userMessage = compressionError.message || 'Failed to process image';
          let errorSeverity: 'S1' | 'S2' | 'S3' = 'S2';

          if (
            compressionError.message?.includes('network') ||
            compressionError.message?.includes('CORS')
          ) {
            errorType = 'network';
            errorSeverity = 'S3'; // Network errors are transient
            userMessage =
              'Network error: Unable to upload receipt. Check your connection and try again.';
          } else if (
            compressionError.message?.includes('size') ||
            compressionError.message?.includes('large')
          ) {
            errorType = 'size';
            errorSeverity = 'S3'; // User error, not system issue
            userMessage = 'File too large: Please use a smaller image (max 10MB).';
          } else if (
            compressionError.message?.includes('format') ||
            compressionError.message?.includes('type')
          ) {
            errorType = 'format';
            errorSeverity = 'S3'; // User error
            userMessage = 'Invalid format: Please use PNG, JPG, or WebP images only.';
          } else if (
            compressionError.code === 'storage/unauthorized' ||
            compressionError.message?.includes('permission') ||
            compressionError.message?.includes('Pro subscription required')
          ) {
            errorType = 'permission';
            errorSeverity = 'S2'; // Permission issues need attention
            userMessage =
              'Permission denied: Your account may not have upload access. Try refreshing the page.';
          } else if (
            compressionError.message?.includes('preflight') ||
            compressionError.code === 'ERR_FAILED'
          ) {
            errorType = 'cors';
            errorSeverity = 'S1'; // CORS errors indicate configuration issues
            userMessage =
              'Upload blocked: Please refresh the page and try again. If the issue persists, contact support.';
          }

          // Log to Sentry with context
          trackError(
            'Receipt upload failed',
            createErrorMeta(
              'storage',
              `RECEIPT_UPLOAD_${errorType.toUpperCase()}` as (typeof ERROR_CODES)[keyof typeof ERROR_CODES],
              errorSeverity,
              'user-blocking',
              errorSeverity === 'S1'
                ? 'immediate'
                : errorSeverity === 'S2'
                  ? '24h'
                  : 'next-release',
              {
                userAction: 'receipt_upload',
                providerCode: compressionError.code,
                extras: {
                  errorType,
                  fileSize: receiptFile.size,
                  fileType: receiptFile.type,
                  uploadDuration: Date.now() - uploadStartTime,
                  userId,
                  expenseId,
                },
              }
            ),
            compressionError
          );

          dispatch(
            setUploadState({
              status: 'failed',
              progress: 0,
              error: `${userMessage} (Error: ${errorType})`,
              lastProgressTime: Date.now(),
            })
          );

          // Log failed compression
          await logReceiptUploadAudit(userId, expenseId, {
            originalBytes: receiptFile.size,
            finalBytes: 0,
            mime: receiptFile.type,
            width: 0,
            height: 0,
            compressionRatio: 1,
            success: false,
            errorMessage: `${errorType}: ${compressionError.message}`,
            uploadDuration: Date.now() - uploadStartTime,
          });

          console.warn(
            '[expenseSlice] ⚠️ Receipt upload failed, continuing WITHOUT receipt:',
            userMessage
          );

          // DON'T throw - continue saving expense without receipt
          // The expense data is more important than the receipt
          receiptPhotoURL = undefined;

          // Notify user that receipt upload failed but expense will still be saved
          dispatch(
            setUploadState({
              status: 'idle',
              progress: 0,
              error: `Receipt upload failed: ${userMessage}. Expense will be saved without receipt.`,
              lastProgressTime: Date.now(),
            })
          );
        }
      }

      // Calculate claimable ITC
      console.log('[expenseSlice] Step 1: Calculating ITC with expenseData:', {
        category: expenseData.category,
        subCategory: expenseData.subCategory,
        amount: expenseData.amount,
        gstPaid: expenseData.gstPaid,
        businessUsePct: expenseData.businessUsePct,
        date: expenseData.date,
      });

      const calculator = new GSTCalculator(provinceCode);
      const claimableITC = calculator.calculateClaimableITC({
        ...expenseData,
        gstPaid: expenseData.gstPaid,
        businessUsePct: expenseData.businessUsePct,
      } as Expense);

      console.log('[expenseSlice] Step 2: ITC calculated:', claimableITC);

      // Convert date to Firestore Timestamp for storage
      // Remove undefined fields (Firestore doesn't allow undefined, must use null or omit)
      const newExpense: any = {
        category: expenseData.category,
        subCategory: expenseData.subCategory,
        amount: expenseData.amount,
        gstPaid: expenseData.gstPaid,
        businessUsePct: expenseData.businessUsePct,
        description: expenseData.description,
        source: expenseData.source,
        date: Timestamp.fromDate(expenseData.date),
        userId,
        receiptPhotoURL,
        claimableITC,
        uploadedAt: Timestamp.now(),
      };

      // Only add optional fields if they have values (not undefined)
      if (expenseData.qualifies50Pct !== undefined) {
        newExpense.qualifies50Pct = expenseData.qualifies50Pct;
      }
      if (expenseData.mealContext !== undefined) {
        newExpense.mealContext = expenseData.mealContext;
      }

      console.log('[expenseSlice] Step 3: Created newExpense object for Firestore:', {
        category: newExpense.category,
        subCategory: newExpense.subCategory,
        amount: newExpense.amount,
        date: newExpense.date,
        userId: newExpense.userId,
        hasAllFields: !!(newExpense.category && newExpense.subCategory && newExpense.amount),
      });

      const expensesRef = collection(db, 'users', userId, 'expenses');
      console.log(
        '[expenseSlice] Step 4: About to save to Firestore path:',
        `users/${userId}/expenses`
      );

      const docRef = await addDoc(expensesRef, newExpense);

      console.log('[expenseSlice] Step 5: Saved to Firestore with ID:', docRef.id);

      expenseId = docRef.id; // Now we have the real ID

      // Log successful upload if there was compression
      if (compressionResult) {
        await logReceiptUploadAudit(userId, expenseId, {
          originalBytes: compressionResult.originalBytes,
          finalBytes: compressionResult.finalBytes,
          mime: compressionResult.mime,
          width: compressionResult.width,
          height: compressionResult.height,
          compressionRatio: compressionResult.ratio,
          exifSummary: compressionResult.exifSummary,
          success: true,
          uploadDuration: Date.now() - uploadStartTime,
        });

        // Track upload bytes for metrics
        trackUsage('usage_upload_bytes', { bytes: compressionResult.finalBytes }).catch(
          console.warn
        );
      }

      console.log('[expenseSlice] Step 6: Building returnExpense object from expenseData:', {
        'expenseData.category': expenseData.category,
        'expenseData.subCategory': expenseData.subCategory,
        'expenseData.amount': expenseData.amount,
        'expenseData.date': expenseData.date,
        'docRef.id': docRef.id,
      });

      // CRITICAL: Return ONLY serializable data to Redux
      // Use Date objects NOT Firestore Timestamps - Redux will corrupt the object otherwise!
      const returnExpense: Expense = {
        id: docRef.id,
        userId: userId,
        category: expenseData.category,
        subCategory: expenseData.subCategory,
        amount: expenseData.amount,
        gstPaid: expenseData.gstPaid,
        businessUsePct: expenseData.businessUsePct,
        description: expenseData.description,
        source: expenseData.source,
        date: expenseData.date, // Date object - serializable
        receiptPhotoURL: receiptPhotoURL,
        claimableITC: claimableITC,
        uploadedAt: new Date(), // MUST be Date object, NOT Firestore Timestamp!
        qualifies50Pct: expenseData.qualifies50Pct,
        mealContext: expenseData.mealContext,
      };

      console.log('[expenseSlice] Step 7: Built returnExpense object:', {
        id: returnExpense.id,
        category: returnExpense.category,
        subCategory: returnExpense.subCategory,
        amount: returnExpense.amount,
        date: returnExpense.date,
        dateType: typeof returnExpense.date,
        dateIsDate: returnExpense.date instanceof Date,
        gstPaid: returnExpense.gstPaid,
        businessUsePct: returnExpense.businessUsePct,
        allKeysCount: Object.keys(returnExpense).length,
      });

      // Validate data integrity before returning
      if (!returnExpense.category || !returnExpense.subCategory) {
        const errorMsg = `Data integrity error: Missing required fields. Category: ${returnExpense.category}, SubCategory: ${returnExpense.subCategory}`;
        console.error('[expenseSlice] CRITICAL:', errorMsg, {
          expenseData,
          returnExpense,
        });
        trackError(
          'Expense data integrity error',
          createErrorMeta(
            'storage',
            ERROR_CODES.DATA_CORRUPTION,
            'S1',
            'user-blocking',
            'immediate',
            {
              userAction: 'add_expense',
              extras: {
                userId,
                expenseId: docRef.id,
                missingCategory: !returnExpense.category,
                missingSubCategory: !returnExpense.subCategory,
              },
            }
          ),
          new Error(errorMsg)
        );
        throw new Error('Failed to save expense: Data integrity error. Please try again.');
      }

      console.log('[expenseSlice] Step 8: ✅ Validation passed, returning expense to Redux:', {
        id: returnExpense.id,
        category: returnExpense.category,
        subCategory: returnExpense.subCategory,
        amount: returnExpense.amount,
        date: returnExpense.date,
        stringified: JSON.stringify(returnExpense, (key, value) => {
          if (value instanceof Date) return value.toISOString();
          if (value?.toMillis) return `Timestamp(${value.toMillis()})`;
          return value;
        }).substring(0, 200),
      });

      return returnExpense;
    } catch (error: any) {
      // Update state to failed
      dispatch(
        setUploadState({
          status: 'failed',
          progress: 0,
          error: error.message || 'Upload failed',
          lastProgressTime: Date.now(),
        })
      );

      // Log failure
      if (receiptFile) {
        await logReceiptUploadAudit(userId, expenseId, {
          originalBytes: receiptFile.size,
          finalBytes: 0,
          mime: receiptFile.type,
          width: 0,
          height: 0,
          compressionRatio: 1,
          success: false,
          errorMessage: error.message,
          uploadDuration: Date.now() - uploadStartTime,
        });
      }

      // Track upload error
      const errorMeta = createErrorMeta(
        'uploads',
        error.code?.includes('storage')
          ? ERROR_CODES.UPLOAD_FAIL_RETRYED
          : ERROR_CODES.UPLOAD_STALLED,
        'S2',
        'user-blocking',
        '24h',
        {
          retryable: true,
          providerCode: error.code,
          userAction: 'upload_receipt',
          extras: {
            uploadDuration: Date.now() - uploadStartTime,
            hadReceiptFile: !!receiptFile,
            fileSize: receiptFile?.size,
          },
        }
      );
      trackError('Failed to add expense with receipt upload', errorMeta, error);

      return rejectWithValue(error.message);
    }
  }
);

export const updateExpense = createAsyncThunk(
  'expenses/updateExpense',
  async (
    {
      userId,
      expenseId,
      updates,
      provinceCode,
    }: {
      userId: string;
      expenseId: string;
      updates: Partial<Expense>;
      provinceCode: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Assert write access with fresh token
      await assertWriteAccess();

      const state = getState() as { expenses: ExpenseState };
      const existingExpense = state.expenses.expenses.find(e => e.id === expenseId);

      if (!existingExpense) {
        throw new Error('Expense not found');
      }

      // Recalculate ITC if GST or business use percentage changed
      let claimableITC = existingExpense.claimableITC;
      if (updates.gstPaid !== undefined || updates.businessUsePct !== undefined) {
        const calculator = new GSTCalculator(provinceCode);
        claimableITC = calculator.calculateClaimableITC({
          ...existingExpense,
          ...updates,
        } as Expense);
      }

      const updatedData = {
        ...updates,
        claimableITC,
        updatedAt: Timestamp.now(),
      };

      const expenseRef = doc(db, 'users', userId, 'expenses', expenseId);
      await updateDoc(expenseRef, updatedData);

      return {
        ...existingExpense,
        ...updatedData,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteExpense = createAsyncThunk(
  'expenses/deleteExpense',
  async (
    { userId, expenseId }: { userId: string; expenseId: string },
    { getState, rejectWithValue }
  ) => {
    try {
      // Assert write access with fresh token
      await assertWriteAccess();

      const state = getState() as { expenses: ExpenseState };
      const expense = state.expenses.expenses.find(e => e.id === expenseId);

      // Delete receipt image if exists
      if (expense?.receiptPhotoURL) {
        try {
          const imageRef = ref(storage, expense.receiptPhotoURL);
          await deleteObject(imageRef);
        } catch (error) {
          console.warn('Failed to delete receipt image:', error);
        }
      }

      // Delete expense document
      const expenseRef = doc(db, 'users', userId, 'expenses', expenseId);
      await deleteDoc(expenseRef);

      return expenseId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const expenseSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<ExpenseState['filters']>) => {
      state.filters = action.payload;
    },
    clearFilters: state => {
      state.filters = {};
    },
    setUploadState: (state, action: PayloadAction<Partial<ExpenseState['uploadState']>>) => {
      state.uploadState = { ...state.uploadState, ...action.payload };
    },
    clearUploadState: state => {
      state.uploadState = {
        status: 'idle',
        progress: 0,
        error: null,
        lastProgressTime: Date.now(),
      };
    },
    // Optimistic upsert into current page list and recompute totals
    upsertExpenseLocal: (state, action: PayloadAction<Expense>) => {
      const item = action.payload;
      const index = state.expenses.findIndex(e => e.id === item.id);
      if (index >= 0) {
        state.expenses[index] = item;
      } else {
        state.expenses.unshift(item);
        if (state.expenses.length > 10) state.expenses = state.expenses.slice(0, 10);
      }
      // Deterministic sort: date desc, uploadedAt desc
      state.expenses = [...state.expenses].sort((a, b) => {
        const ad = a.date instanceof Date ? a.date.getTime() : ((a as any).date?.toMillis?.() ?? 0);
        const bd = b.date instanceof Date ? b.date.getTime() : ((b as any).date?.toMillis?.() ?? 0);
        if (bd !== ad) return bd - ad;
        // uploadedAt is now a Date object, not Firestore Timestamp
        const a2 =
          (a as any).createdAt?.toMillis?.() ??
          (a.uploadedAt instanceof Date
            ? a.uploadedAt.getTime()
            : ((a as any).uploadedAt?.toMillis?.() ?? 0));
        const b2 =
          (b as any).createdAt?.toMillis?.() ??
          (b.uploadedAt instanceof Date
            ? b.uploadedAt.getTime()
            : ((b as any).uploadedAt?.toMillis?.() ?? 0));
        return b2 - a2;
      });
    },
    recomputeExpenseAggregates: state => {
      state.totalExpenses = state.expenses.reduce((sum, e) => sum + e.amount, 0);
      state.totalITCs = state.expenses.reduce((sum, e) => sum + e.claimableITC, 0);
    },
  },
  extraReducers: builder => {
    builder
      // Fetch expenses
      .addCase(fetchExpenses.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenses = action.payload;
        state.totalExpenses = action.payload.reduce((sum, expense) => sum + expense.amount, 0);
        state.totalITCs = action.payload.reduce((sum, expense) => sum + expense.claimableITC, 0);
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Add expense
      .addCase(addExpense.pending, state => {
        state.isLoading = true;
        state.error = null;
        // Reset upload state for new upload
        state.uploadState = {
          status: 'idle',
          progress: 0,
          error: null,
          lastProgressTime: Date.now(),
        };
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        console.log('[expenseSlice REDUCER] Step 9: Received payload in reducer:', {
          payloadExists: !!action.payload,
          payloadType: typeof action.payload,
          payloadKeys: action.payload ? Object.keys(action.payload) : [],
          id: action.payload?.id,
          category: action.payload?.category,
          subCategory: action.payload?.subCategory,
          amount: action.payload?.amount,
          date: action.payload?.date,
        });

        const payload = action.payload;

        console.log('[expenseSlice REDUCER] Step 10: Payload after extraction:', {
          'payload.id': payload.id,
          'payload.category': payload.category,
          'payload.subCategory': payload.subCategory,
          'payload.amount': payload.amount,
          'payload.date': payload.date,
          'payload.dateType': typeof payload.date,
        });

        // Validate payload data integrity
        if (!payload.category || !payload.subCategory) {
          console.error('[expenseSlice REDUCER] CRITICAL: Received invalid expense payload!', {
            id: payload.id,
            category: payload.category,
            subCategory: payload.subCategory,
            amount: payload.amount,
            date: payload.date,
            allKeys: Object.keys(payload),
            payloadType: typeof payload,
            fullPayload: JSON.stringify(payload, (key, value) => {
              if (value instanceof Date) return value.toISOString();
              if (value?.toMillis) return `Timestamp(${value.toMillis()})`;
              return value;
            }),
          });
          state.isLoading = false;
          state.uploadState.status = 'failed';
          state.error = 'Data integrity error: Expense missing required fields';
          return;
        }

        // Ensure date is a proper Date object
        const safeExpense = {
          ...payload,
          date: payload.date instanceof Date ? payload.date : new Date(payload.date),
        };

        console.log('[expenseSlice REDUCER] Step 11: Created safeExpense:', {
          id: safeExpense.id,
          category: safeExpense.category,
          subCategory: safeExpense.subCategory,
          amount: safeExpense.amount,
          date: safeExpense.date,
          dateType: typeof safeExpense.date,
          dateIsDate: safeExpense.date instanceof Date,
          hasAllRequiredFields: !!(
            safeExpense.category &&
            safeExpense.subCategory &&
            safeExpense.amount
          ),
        });

        state.isLoading = false;
        state.uploadState.status = 'done';
        state.uploadState.progress = 100;

        console.log('[expenseSlice REDUCER] Step 12: About to add expense to state.expenses array');

        // Optimistically show new expense at top of first page
        state.expenses.unshift(safeExpense);
        state.totalExpenses += safeExpense.amount;
        state.totalITCs += safeExpense.claimableITC;

        console.log('[expenseSlice REDUCER] Step 13: ✅ Expense added to state:', {
          newExpensesCount: state.expenses.length,
          firstExpenseId: state.expenses[0]?.id,
          firstExpenseCategory: state.expenses[0]?.category,
          firstExpenseSubCategory: state.expenses[0]?.subCategory,
          firstExpenseDate: state.expenses[0]?.date,
        });
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.uploadState.status = 'failed';
        state.uploadState.error = action.payload as string;
      })

      // Update expense
      .addCase(updateExpense.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.expenses.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          const oldExpense = state.expenses[index];
          state.expenses[index] = action.payload;

          // Update totals
          state.totalExpenses = state.totalExpenses - oldExpense.amount + action.payload.amount;
          state.totalITCs = state.totalITCs - oldExpense.claimableITC + action.payload.claimableITC;
        }
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete expense
      .addCase(deleteExpense.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.isLoading = false;
        const expenseIndex = state.expenses.findIndex(e => e.id === action.payload);
        if (expenseIndex !== -1) {
          const deletedExpense = state.expenses[expenseIndex];
          state.expenses.splice(expenseIndex, 1);
          state.totalExpenses -= deletedExpense.amount;
          state.totalITCs -= deletedExpense.claimableITC;
        }
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Paged
      .addCase(fetchExpensesPaged.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpensesPaged.fulfilled, (state, action) => {
        state.isLoading = false;
        const { items, nextCursor } = action.payload as any;
        state.expenses = items;
        state.paging!.nextCursor = nextCursor;
        state.totalExpenses = items.reduce((sum: number, e: Expense) => sum + e.amount, 0);
        state.totalITCs = items.reduce((sum: number, e: Expense) => sum + e.claimableITC, 0);
      })
      .addCase(fetchExpensesPaged.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  setUploadState,
  clearUploadState,
  upsertExpenseLocal,
  recomputeExpenseAggregates,
} = expenseSlice.actions;
export default expenseSlice.reducer;
